import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  UserPlus,
  Search,
  Users,
  Activity,
  Stethoscope,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Pill,
  Receipt,
  ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

function StatCard({ label, value, icon: Icon, tone = "forest", testid }) {
  const toneMap = {
    forest: "bg-[#1A4331]/8 text-forest",
    turmeric: "bg-[#E5A93D]/12 text-[#B57F1D]",
    terra: "bg-[#C25946]/10 text-[#8F3C2C]",
    sand: "bg-[#F0EBDE] text-[#5C5652]",
  };
  return (
    <div
      data-testid={testid}
      className="p-6 rounded-lg bg-white border border-sand hover:-translate-y-0.5 hover:border-[#1A4331]/30 transition-transform transition-colors"
    >
      <div className={`inline-flex items-center justify-center h-10 w-10 rounded-lg ${toneMap[tone]} mb-4`}>
        <Icon className="h-5 w-5" strokeWidth={1.5} />
      </div>
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C5652] mb-1">
        {label}
      </div>
      <div className="font-display text-4xl text-forest leading-none">{value}</div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex items-start gap-4 p-5 rounded-lg bg-white border border-sand hover:border-[#1A4331]/40 hover:-translate-y-0.5 transition-transform transition-colors"
    >
      <div className="h-11 w-11 rounded-lg bg-forest text-white flex items-center justify-center flex-shrink-0 group-hover:bg-forest-hover transition-colors">
        <Icon className="h-5 w-5" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg text-forest mb-1">{title}</div>
        <div className="text-sm text-[#5C5652]">{desc}</div>
      </div>
      <ArrowRight className="h-4 w-4 text-[#5C5652] mt-3 group-hover:translate-x-0.5 group-hover:text-forest transition-transform transition-colors" strokeWidth={1.75} />
    </Link>
  );
}

const ROLE_INTRO = {
  admin: {
    title: "Front office — patient in-take",
    desc: "Register walk-ins, search records, and hand patients over to the nursing queue.",
  },
  nurse: {
    title: "Nursing station",
    desc: "Record vitals and hand patients over to their consulting doctor.",
  },
  doctor: {
    title: "Consultation room",
    desc: "See today's queue, call your next patient, and file diagnoses & prescriptions.",
  },
  pharmacy: {
    title: "Dispensary",
    desc: "Dispense prescribed medicines and print intake charts.",
  },
  billing: {
    title: "Pharmacy billing",
    desc: "Generate bills and issue pharmacy tokens to patients.",
  },
  manager: {
    title: "Hospital manager",
    desc: "Track admissions, room occupancy, discharge summaries and end-of-day reports.",
  },
  superadmin: {
    title: "Superadmin",
    desc: "Full access to every branch, role and record. Configure the system.",
  },
};

export default function Dashboard() {
  const { user, branch } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          api.get(`/stats/dashboard`, { params: { branch_code: branch?.code } }),
          api.get(`/patients`, { params: { branch_code: branch?.code } }),
        ]);
        setStats(s.data);
        setRecent(p.data.slice(0, 6));
      } catch (err) {
        toast.error(formatApiError(err, "Could not load dashboard"));
      } finally {
        setLoading(false);
      }
    })();
  }, [branch?.code]);

  const intro = ROLE_INTRO[user?.role] || ROLE_INTRO.admin;
  const role = user?.role;

  const quickActions = [];
  if (role === "admin" || role === "superadmin") {
    quickActions.push(
      { to: "/patients/new", icon: UserPlus, title: "Register a patient", desc: "New walk-in or first visit — capture the basics.", testid: "quick-register" },
      { to: "/patients", icon: Search, title: "Search & re-register", desc: "Look up a returning patient by name, phone or MRN.", testid: "quick-search" },
    );
  }
  if (role === "nurse") {
    quickActions.push(
      { to: "/patients", icon: Search, title: "Find a patient", desc: "Search today's patients allocated to nursing.", testid: "quick-search" },
    );
  }
  if (role === "doctor") {
    quickActions.push(
      { to: "/patients", icon: Search, title: "Patient records", desc: "Search across today's patients.", testid: "quick-search" },
    );
  }
  if (role === "manager") {
    quickActions.push(
      { to: "/patients", icon: Search, title: "Patient records", desc: "Cross-branch patient search.", testid: "quick-search" },
    );
  }

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="dashboard-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
          {branch?.name} · {branch?.specialty}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl text-forest leading-tight mb-2">
          Good {greetingByHour()}, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="text-[#5C5652] max-w-2xl">{intro.desc}</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:gap-6 grid-cols-2 lg:grid-cols-4 mb-10">
        <StatCard
          testid="stat-total-today"
          label="Patients today"
          value={loading ? "–" : stats?.total_today ?? 0}
          icon={Users}
          tone="forest"
        />
        <StatCard
          testid="stat-in-nursing"
          label="In nursing"
          value={loading ? "–" : stats?.in_nursing ?? 0}
          icon={Activity}
          tone="turmeric"
        />
        <StatCard
          testid="stat-in-consultation"
          label="In consultation"
          value={loading ? "–" : stats?.in_consultation ?? 0}
          icon={Stethoscope}
          tone="sand"
        />
        <StatCard
          testid="stat-done"
          label="Completed"
          value={loading ? "–" : stats?.done ?? 0}
          icon={CheckCircle2}
          tone="forest"
        />
      </div>

      {/* Quick actions */}
      {quickActions.length > 0 && (
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-4 ornament-dot">
            Quick actions
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {quickActions.map((a) => (
              <QuickAction key={a.to} {...a} />
            ))}
          </div>
        </div>
      )}

      {/* Recent patients */}
      <div className="mb-10">
        <div className="flex items-baseline justify-between mb-4">
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] ornament-dot">
            Recent patients — {branch?.code}
          </div>
          <Link to="/patients" className="text-forest text-sm font-semibold hover:underline" data-testid="see-all-patients">
            See all →
          </Link>
        </div>

        <div className="bg-white border border-sand rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-sm text-[#5C5652]">Loading…</div>
          ) : recent.length === 0 ? (
            <div className="p-10 text-center">
              <div className="font-display text-forest text-xl mb-1">No patients yet today</div>
              <p className="text-sm text-[#5C5652] mb-4">
                Start by registering your first patient at this branch.
              </p>
              {(role === "admin" || role === "superadmin") && (
                <Link
                  to="/patients/new"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover transition-colors"
                  data-testid="empty-register-cta"
                >
                  <UserPlus className="h-4 w-4" strokeWidth={1.75} /> Register a patient
                </Link>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-[#E8E3D9]" data-testid="recent-patients-list">
              {recent.map((p) => (
                <li key={p.id} className="px-6 py-4 flex items-center gap-4 hover:bg-[#F9F6F0] transition-colors">
                  <div className="h-9 w-9 rounded-full bg-[#1A4331]/10 text-forest flex items-center justify-center text-sm font-bold">
                    {p.name?.slice(0, 1)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#2A2624] truncate">{p.name}</div>
                    <div className="text-xs text-[#5C5652]">
                      {p.age} · {p.gender} · {p.phone}
                    </div>
                  </div>
                  <div className="hidden sm:block text-right">
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Token</div>
                    <div className="font-display text-forest">{p.op_token}</div>
                  </div>
                  <StatusPill status={p.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Phase preview */}
      <div className="p-6 rounded-lg border border-sand bg-[#1A4331]/[0.03]">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-lg bg-forest text-white flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-display text-forest text-lg mb-1">Phase 1 · Live</div>
            <p className="text-sm text-[#5C5652]">
              You are on the first phase — login, branch selection, dashboards and patient
              registration. Upcoming phases add nursing vitals, doctor consultation with AI voice
              diagnosis, pharmacy billing, and manager tools.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { l: "Vitals & queue", i: Activity, p: 2 },
                { l: "Consultation & AI", i: Stethoscope, p: 3 },
                { l: "Pharmacy & billing", i: Pill, p: 4 },
                { l: "Billing tokens", i: Receipt, p: 4 },
                { l: "Manager · IP · reports", i: ClipboardList, p: 5 },
              ].map((x) => (
                <span key={x.l} className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-sand bg-white text-[#5C5652]">
                  <x.i className="h-3 w-3" strokeWidth={1.75} />
                  {x.l}
                  <span className="text-[#E5A93D]">· P{x.p}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

function StatusPill({ status }) {
  const map = {
    registered: { bg: "bg-[#F0EBDE]", text: "text-[#5C5652]", label: "Registered" },
    in_nursing: { bg: "bg-[#E5A93D]/15", text: "text-[#B57F1D]", label: "In nursing" },
    in_consultation: { bg: "bg-[#1A4331]/10", text: "text-forest", label: "In consult" },
    done: { bg: "bg-[#1A4331]", text: "text-white", label: "Done" },
  };
  const s = map[status] || map.registered;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}
